import React from "react";
import type {
  ProductCardBlock,
  ProductCardVariant,
} from "../../../types/content";
import { getVariantLabel } from "@/types/product";
import { useIsB2b } from "@/hooks/useIsB2b";
import { useProductSku } from "@/hooks/useProductSku";
import { useProductAddToCart } from "./useProductAddToCart";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { ProductSku } from "@/components/common/ProductSku/ProductSku";
import { isProductDiscounted } from "@/utils/discount";
import { CartIcon } from "../../icons";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { useLocalizedTexts, t } from "@/utils/translations";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./ProductCard.module.css";

/**
 * The card fills whatever holds it (a block grid span, a listing column) and
 * does not know its width, so the lazy image leads with `auto` and the browser
 * uses its laid-out width. Browsers without `sizes=auto` get the widest case:
 * for a small card the 280px cap, lifted below 992px where it fills a 4-of-8
 * span or the single mobile column; else a 6-of-12 block grid span (572px).
 */
const SMALL_CARD_IMAGE_SIZES =
  "auto, (max-width: 576px) 100vw, (max-width: 992px) 50vw, 280px";
const WIDE_CARD_IMAGE_SIZES =
  "auto, (max-width: 576px) 100vw, (max-width: 1200px) 50vw, 572px";

export interface ProductCardProps {
  block: ProductCardBlock;
  className?: string;
  /** Override the card variant from the block */
  variantOverride?: ProductCardVariant;
}

/**
 * Product Card component - displays product with image, details, and add-to-cart
 * Supports small and large size variants
 * Supports detailed (default) and minimal variants
 */
export const ProductCard: React.FC<ProductCardProps> = ({
  block,
  className,
  variantOverride,
}) => {
  const localizedTexts = useLocalizedTexts();
  const {
    image,
    imageAlt,
    category,
    productName,
    description,
    price,
    priceInfo,
    productLink,
    productCardSize = "small",
    cardVariant = "detailed",
  } = block;

  // Use override if provided, otherwise use block's variant
  const variant = variantOverride ?? cardVariant;

  const isB2b = useIsB2b();
  const sku = useProductSku(block);
  // `selectedVariant` (in the hook) is the product version being bought — not
  // to be confused with `variant` above, which is this card's visual style.
  const {
    variants,
    hasVariants,
    isAddingToCart,
    isAdded,
    isVariantPickerOpen,
    cartAreaRef,
    handleAddToCart,
    handleAddButtonClick,
  } = useProductAddToCart(block);

  if (!productName) {
    return null;
  }

  const variantClass =
    variant === "minimal"
      ? styles.cardMinimal
      : variant === "showcase"
        ? styles.cardShowcase
        : styles.cardDetailed;

  const containerClasses = [styles.card, variantClass, className]
    .filter(Boolean)
    .join(" ");

  // Get product URL from link (localized with current locale prefix)
  const currentLocale = getLocale().language;
  const productUrl = productLink
    ? resolveHref(productLink, currentLocale)
    : "#";

  // Button state and label
  const getButtonLabel = () => {
    if (isAddingToCart)
      return t(localizedTexts, "Tracker.Products.Adding", "Adding...");
    if (isAdded) return t(localizedTexts, "Tracker.Products.Added", "Added!");
    return t(localizedTexts, "Tracker.Products.AddToCart", "Add to cart");
  };

  /**
   * The card's price, shown on every variant directly under the product name.
   * B2B retailers see the recommended consumer price above their net price; consumers
   * see the struck original above the discounted price when the product is on
   * offer; otherwise the plain formatted price.
   */
  const hasB2bBreakdown =
    isB2b &&
    !!priceInfo &&
    !!(
      priceInfo.formattedListPrice ||
      priceInfo.formattedCalculatedPriceWithoutVat
    );
  const hasConsumerDiscount =
    !isB2b &&
    isProductDiscounted(priceInfo) &&
    !!priceInfo?.formattedListPrice &&
    !!priceInfo?.formattedCalculatedPrice;

  const priceElement = hasB2bBreakdown ? (
    <DiscountedUnitPrice
      variant="b2b"
      standardInclVat={priceInfo?.formattedListPrice}
      accentPrice={priceInfo?.formattedCalculatedPriceWithoutVat}
      size="sm"
    />
  ) : hasConsumerDiscount ? (
    <DiscountedUnitPrice
      variant="discount"
      standardInclVat={priceInfo?.formattedListPrice}
      accentPrice={priceInfo?.formattedCalculatedPrice}
      size="sm"
      inline
    />
  ) : price ? (
    <span className={styles.price}>{price}</span>
  ) : null;

  // GROW-251: B2B members see the SKU directly under the price on every
  // variant, so a retailer knows exactly which article the card sells.
  const skuElement = sku ? (
    <ProductSku sku={sku} className={styles.sku} />
  ) : null;

  // Product name, optionally wrapped in a link
  const productNameElement = productLink?.url ? (
    <h3 className={styles.name}>
      <a
        href={productUrl}
        className={styles.nameLink}
        target={productLink.target || "_self"}
        rel={
          productLink.target === "_blank" ? "noopener noreferrer" : undefined
        }
      >
        {productName}
      </a>
    </h3>
  ) : (
    <h3 className={styles.name}>{productName}</h3>
  );

  // Pulled out of the markup below because the showcase card pairs it with the
  // price in a row, while the other variants stack it under the description.
  const cartArea = (
    <div className={styles.cartArea} ref={cartAreaRef}>
      <button
        type="button"
        className={styles.addToCartButton}
        onClick={handleAddButtonClick}
        disabled={isAddingToCart}
        aria-label={`${t(localizedTexts, "Tracker.Products.AddToCart", "Add to cart")} - ${productName}`}
        aria-haspopup={hasVariants ? "true" : undefined}
        aria-expanded={hasVariants ? isVariantPickerOpen : undefined}
        data-state={isAddingToCart ? "loading" : isAdded ? "added" : "default"}
      >
        <CartIcon className={styles.cartIcon} />
        <span className={styles.cartLabel}>{getButtonLabel()}</span>
      </button>

      {hasVariants && isVariantPickerOpen && (
        <div
          className={styles.variantPicker}
          role="group"
          aria-label={t(
            localizedTexts,
            "Tracker.Products.ChooseVariant",
            "Choose version:"
          )}
        >
          {variants.map((option) => (
            <button
              key={option.id}
              type="button"
              className={styles.variantOption}
              onClick={() => void handleAddToCart(option)}
              disabled={isAddingToCart}
            >
              {getVariantLabel(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <article
      className={containerClasses}
      data-component="product-card"
      data-size={productCardSize}
      data-variant={variant}
    >
      {/* Product Image */}
      <div className={styles.imageWrapper}>
        {image ? (
          <img
            {...responsiveCmsImage(
              image.url,
              productCardSize === "small" && variant !== "showcase"
                ? SMALL_CARD_IMAGE_SIZES
                : WIDE_CARD_IMAGE_SIZES,
              image.width
            )}
            alt={imageAlt || productName}
            className={styles.image}
            loading="lazy"
          />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>

      {/* Content Area */}
      <div className={styles.content}>
        {/* Category eyebrow - hidden on the minimal variant */}
        {variant !== "minimal" && category && (
          <span className={styles.category}>{category}</span>
        )}

        {/* Product name */}
        {productNameElement}

        {variant === "showcase" ? (
          <>
            {/* The showcase card reads description-then-price, and pairs the
                price with the CTA so a phone can sit them side by side. */}
            {description && <p className={styles.description}>{description}</p>}
            <div className={styles.priceRow}>
              {/* The SKU stacks under the price, so on a phone the pair still
                  sits beside the round CTA. Only wrapped when there is a SKU,
                  which leaves the consumer card's markup untouched. */}
              {skuElement ? (
                <div className={styles.priceStack}>
                  {priceElement}
                  {skuElement}
                </div>
              ) : (
                priceElement
              )}
              {cartArea}
            </div>
          </>
        ) : (
          <>
            {/* Price - directly under the name */}
            {priceElement}

            {skuElement}

            {/* Description - only shown in detailed variant */}
            {variant === "detailed" && description && (
              <p className={styles.description}>{description}</p>
            )}

            {cartArea}
          </>
        )}
      </div>
    </article>
  );
};

export default ProductCard;
